import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from "next";

import { AppShell } from "@chef/feature-app-shell";
import { Mealcards } from "@chef/smart-components";
import { useRecipesByIdsQuery } from "@chef/state-management";
import { Tag } from "@chef/components";
import {
  BlockContent,
  SanityQuery,
  useSharedMenuData,
  ISharedMenu,
  getSharedMenuData,
} from "@chef/feature-sanity";
import { applyGenitiveCase } from "@chef/helpers";
import { isEmptyArray } from "@chef/utils/array";

import { brand, intl } from "./SharedMenu.Intl";

type Data = {
  ids: number[];
  portions: number;
  meals: number;
  name: string;
  week: number;
  year: number;
};

export const SharedMenuPage = ({
  sharedMenu,
  data,
}: InferGetStaticPropsType<typeof SharedMenuPageGetStaticProps>) => {
  const [sharedMenuData] = useSharedMenuData(sharedMenu);

  const {
    topSection,
    bottomSection,
    showDefaultGreeting = true,
  } = sharedMenuData || {};

  const { name, ids, meals, portions, week, year } = data;

  const { data: recipesByIdsQuery } = useRecipesByIdsQuery(
    {
      recipeIds: ids,
    },
    {
      skip: !ids || isEmptyArray(ids),
    },
  );

  const recipes = recipesByIdsQuery?.recipesByIds;

  const sharerName = applyGenitiveCase(name);

  if (!recipes) {
    return null;
  }

  return (
    <AppShell title={`${intl.CHECK_OUT} ${sharerName} ${intl.MENU}`}>
      <AppShell.Meta />
      <AppShell.Drawer />
      <AppShell.Header />

      <AppShell.Main grid gradient>
        {topSection && (
          <div className="col-span-full">
            <BlockContent body={topSection} prose={{ padding: "none" }} />
          </div>
        )}

        {showDefaultGreeting && (
          <div className="mb-6 text-center col-span-full">
            <h1 className="mb-6 ">
              <strong>
                {intl.CHECK_OUT} {sharerName} {intl.MENU}
              </strong>
            </h1>
            <p>
              {intl.LOOK_WHAT_I_HAVE_ORDERED_FOR_WEEK} {week}.
            </p>
            <p className="mt-4 text-sm">
              {brand.YOU_RECEIVE_THIS_MENU_FROM_ACTIVE_USER_OF}
            </p>
          </div>
        )}

        <div className="col-span-full xl:col-span-9">
          <div className="mb-3 space-x-2">
            <Tag>
              {meals} {intl.DINNERS}
            </Tag>
            <Tag>
              {portions} {intl.PORTIONS}
            </Tag>
          </div>

          <Mealcards
            recipes={recipes}
            week={week}
            year={year}
            portions={portions}
            meals={meals}
            isDelivered={false}
            view="conversion"
          />
        </div>

        {bottomSection && (
          <div className="col-span-full">
            <BlockContent body={bottomSection} prose={{ padding: "none" }} />
          </div>
        )}
      </AppShell.Main>
      <AppShell.Footer />
      <AppShell.Sticky anchor="bottom">
        <AppShell.TabBar />
      </AppShell.Sticky>
    </AppShell>
  );
};

export const SharedMenuPageGetStaticProps: GetStaticProps<
  {
    draftMode: boolean;
    sharedMenu: ISharedMenu | null;
    data: Data;
  },
  SanityQuery
> = async ({ draftMode = false, params }) => {
  const data = params?.["data"] as string;

  const decoded = Buffer.from(data, "base64").toString();

  const searchParams = new URLSearchParams(decoded);

  const ids = searchParams.getAll("ids");
  const portions = searchParams.get("portions");
  const meals = searchParams.get("meals");
  const name = searchParams.get("name");
  const week = searchParams.get("week");
  const year = searchParams.get("year");
  const agreementId = searchParams.get("agreementId");

  if (!ids || !portions || !meals || !name || !week || !year || !agreementId) {
    return {
      notFound: true,
      props: {
        draftMode: draftMode || false,
        sharedMenu: null,
        data: null,
      },
    };
  }

  const sharedMenu = await getSharedMenuData(agreementId, {
    preview: draftMode || false,
  });

  return {
    props: {
      draftMode: draftMode || false,
      sharedMenu,
      data: {
        ids: ids.map((id) => +id),
        portions: +portions,
        meals: +meals,
        name,
        week: +week,
        year: +year,
      },
    },
  };
};

export const SharedMenuPageGetStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};
